import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fullUrl } from "../../lib/api";

const EMPTY_POST = {
    title: "", slug: "", excerpt: "", body: "", image: "",
    category: "", tags: "", meta_title: "", meta_desc: "", meta_keywords: "",
    published: true,
};

export const ImagePicker = ({ value, onChange, testid }) => {
    const [uploading, setUploading] = useState(false);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("تم رفع الصورة");
        } catch {
            toast.error("تعذر رفع الصورة");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="sm:col-span-2 space-y-2">
            <div className="flex gap-2">
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="رابط الصورة أو ارفع ملفاً"
                    data-testid={testid}
                    className="flex-1 bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60"
                    dir="ltr"
                />
                <label
                    className={`flex items-center gap-2 bg-[#1B2A6B] text-white text-xs font-bold rounded-xl px-4 cursor-pointer hover:bg-[#253C96] transition-colors duration-300 ${uploading ? "opacity-50" : ""}`}
                    data-testid={`${testid}-upload`}
                >
                    <Upload className="w-3.5 h-3.5" />
                    {uploading ? "جاري الرفع..." : "رفع"}
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
                </label>
            </div>
            {value && (
                <img src={fullUrl(value)} alt="معاينة" className="h-20 rounded-xl border border-white/10 object-cover" />
            )}
        </div>
    );
};

export const PostsPanel = () => {
    const [posts, setPosts] = useState([]);
    const [cats, setCats] = useState([]);
    const [modal, setModal] = useState(null);

    const load = useCallback(() => {
        api.get("/posts/all").then((r) => setPosts(r.data)).catch(() => {});
        api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (p) => {
        if (!window.confirm(`حذف مقال «${p.title}»؟`)) return;
        try {
            await api.delete(`/posts/${p.id}`);
            toast.success("تم حذف المقال");
            load();
        } catch {
            toast.error("تعذر الحذف");
        }
    };

    return (
        <div data-testid="admin-posts-panel">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-bold text-xl text-white">المقالات والمدونة</h2>
                <button
                    onClick={() => setModal({ ...EMPTY_POST })}
                    data-testid="admin-add-post-button"
                    className="flex items-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-full px-5 py-2.5 hover:bg-amber-300 transition-colors duration-300"
                >
                    <Plus className="w-4 h-4" /> مقال جديد
                </button>
            </div>

            <div className="border border-white/10 rounded-3xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]">
                        <thead>
                            <tr className="bg-[#101B3D] text-slate-400 text-xs">
                                <th className="text-right py-3.5 px-5 font-bold">العنوان</th>
                                <th className="text-right py-3.5 px-5 font-bold">التصنيف</th>
                                <th className="text-right py-3.5 px-5 font-bold">الحالة</th>
                                <th className="text-right py-3.5 px-5 font-bold">إجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {posts.length === 0 && (
                                <tr><td colSpan="4" className="py-10 text-center text-slate-500">لا توجد مقالات بعد</td></tr>
                            )}
                            {posts.map((p) => (
                                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-post-row-${p.id}`}>
                                    <td className="py-3.5 px-5">
                                        <p className="font-bold text-white">{p.title}</p>
                                        <p className="text-[11px] text-slate-600" dir="ltr">/blog/{p.slug}</p>
                                    </td>
                                    <td className="py-3.5 px-5 text-slate-400 text-xs">{p.category || "—"}</td>
                                    <td className="py-3.5 px-5">
                                        {p.published ? (
                                            <span className="text-[11px] font-bold text-emerald-400">منشور</span>
                                        ) : (
                                            <span className="text-[11px] font-bold text-slate-500">مسودة</span>
                                        )}
                                    </td>
                                    <td className="py-3.5 px-5">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setModal({ ...p, tags: (p.tags || []).join("، ") })}
                                                data-testid={`admin-edit-post-${p.id}`}
                                                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-amber-400 hover:border-amber-400/50 transition-colors duration-200"
                                                aria-label="تعديل"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => remove(p)}
                                                data-testid={`admin-delete-post-${p.id}`}
                                                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors duration-200"
                                                aria-label="حذف"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AnimatePresence>
                {modal && <PostFormModal initial={modal} cats={cats} onClose={() => setModal(null)} onSaved={load} />}
            </AnimatePresence>
        </div>
    );
};

function PostFormModal({ initial, cats, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const inputCls =
        "w-full bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = {
            ...form,
            tags: typeof form.tags === "string" ? form.tags.split(/[،,]/).map((s) => s.trim()).filter(Boolean) : form.tags,
        };
        delete payload.id;
        delete payload.created_at;
        try {
            if (isEdit) {
                await api.put(`/posts/${initial.id}`, payload);
                toast.success("تم تحديث المقال");
            } else {
                await api.post("/posts", payload);
                toast.success("تم نشر المقال");
            }
            onSaved();
            onClose();
        } catch {
            toast.error("تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                data-testid="admin-post-form-modal"
                className="w-full max-w-3xl max-h-[90vh] overflow-y-auto spotlight-card border border-white/10 rounded-3xl p-6 sm:p-8"
            >
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display font-bold text-lg text-white">{isEdit ? "تعديل المقال" : "مقال جديد"}</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="post-form-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                    <input required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="عنوان المقال" data-testid="post-title" className={`${inputCls} sm:col-span-2`} />
                    <input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="الرابط (slug) — يُنشأ تلقائياً إن تُرك فارغاً" data-testid="post-slug" className={inputCls} dir="ltr" />
                    <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="post-category" className={inputCls}>
                        <option value="">التصنيف</option>
                        {cats.map((c) => (
                            <option key={c.id} value={c.name_ar}>{c.name_ar}</option>
                        ))}
                    </select>
                    <textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="مقتطف قصير يظهر في البطاقات" data-testid="post-excerpt" className={`${inputCls} sm:col-span-2 h-16 resize-none`} />
                    <textarea required value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="نص المقال الكامل" data-testid="post-body" className={`${inputCls} sm:col-span-2 h-40`} />
                    <ImagePicker value={form.image} onChange={(v) => set("image", v)} testid="post-image" />
                    <input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="الوسوم — افصل بفاصلة" data-testid="post-tags" className={`${inputCls} sm:col-span-2`} />

                    <p className="sm:col-span-2 font-display font-bold text-amber-400 text-sm border-t border-white/10 pt-4">إعدادات SEO</p>
                    <input value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} placeholder="عنوان SEO (يظهر في نتائج جوجل)" data-testid="post-meta-title" className={inputCls} />
                    <input value={form.meta_keywords} onChange={(e) => set("meta_keywords", e.target.value)} placeholder="كلمات مفتاحية — افصل بفاصلة" data-testid="post-meta-keywords" className={inputCls} />
                    <textarea value={form.meta_desc} onChange={(e) => set("meta_desc", e.target.value)} placeholder="وصف SEO (150-160 حرف)" data-testid="post-meta-desc" className={`${inputCls} sm:col-span-2 h-16 resize-none`} />

                    <label className="sm:col-span-2 flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
                        <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} data-testid="post-published" className="w-4 h-4 accent-amber-400" />
                        منشور (يظهر في الموقع)
                    </label>
                    <button type="submit" disabled={saving} data-testid="post-form-submit" className="sm:col-span-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl py-3.5 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50">
                        {saving ? "جاري الحفظ..." : isEdit ? "حفظ التعديلات" : "نشر المقال"}
                    </button>
                </form>
            </motion.div>
        </motion.div>
    );
}
